/**
 * 딥링크 핸들러 훅
 * Capacitor App 플러그인을 사용하여 딥링크(앱 URL 스킴) 이벤트를 처리
 * OAuth 콜백 등 외부에서 앱으로 돌아올 때 사용
 */
import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { setAuthToken } from "@/lib/apollo";
import { DEEPLINK_SCHEME } from "@/lib/constants/app";

/**
 * 딥링크 이벤트를 처리하는 훅
 * OAuth 콜백 등 외부 브라우저에서 앱으로 돌아올 때 토큰 추출 및 저장
 */
export function useDeepLink() {
  const navigate = useNavigate();

  // 딥링크 URL에서 OAuth 토큰 추출
  const extractOAuthToken = useCallback((url: string): string | null => {
    try {
      // URL에서 hash fragment 파싱 (com.koreadcc.ict://oauth/callback#oauth_token=xxx)
      const hashMatch = url.match(/#oauth_token=([^&]+)/);
      if (hashMatch && hashMatch[1]) {
        return decodeURIComponent(hashMatch[1]);
      }

      return null;
    } catch (error) {
      console.error("토큰 추출 실패:", error);
      return null;
    }
  }, []);

  // 딥링크 URL에서 에러 추출
  const extractError = useCallback((url: string): string | null => {
    try {
      const errorMatch = url.match(/[?&]error=([^&#]+)/);
      if (errorMatch && errorMatch[1]) {
        return decodeURIComponent(errorMatch[1]);
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  // 딥링크 URL 처리
  const handleDeepLink = useCallback(
    async (url: string) => {
      console.log("딥링크 수신:", url);

      // OAuth 콜백 경로 확인
      if (url.includes("://oauth/callback") || url.includes("/oauth/callback")) {
        // In-App Browser 닫기
        try {
          await Browser.close();
        } catch {
          // 브라우저가 이미 닫혔거나 열려있지 않은 경우 무시
        }

        // 에러 확인
        const error = extractError(url);
        if (error) {
          console.error("OAuth 에러:", error);
          navigate("/login", {
            replace: true,
            state: { error: `로그인 실패: ${error}` },
          });
          return;
        }

        // 토큰 추출 및 저장
        const token = extractOAuthToken(url);
        if (token) {
          console.log("OAuth 토큰 수신 완료");
          setAuthToken(token);

          // OAuthCallbackPage로 이동하여 인증 확인 후 /projects로 이동
          // (URL hash에 토큰 포함하여 OAuthCallbackPage에서 처리 가능하도록)
          navigate("/oauth/callback", { replace: true });
        } else {
          console.warn("토큰을 찾을 수 없음:", url);
          navigate("/login", {
            replace: true,
            state: { error: "로그인 토큰을 받지 못했습니다." },
          });
        }
      }
    },
    [navigate, extractOAuthToken, extractError]
  );

  useEffect(() => {
    // 네이티브 환경에서만 딥링크 리스너 등록
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // 앱이 URL로 열렸을 때 처리
    const listener = App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      // 우리 앱의 딥링크인지 확인
      if (event.url.startsWith(DEEPLINK_SCHEME)) {
        handleDeepLink(event.url);
      }
    });

    // 앱이 이미 열린 상태에서 URL로 포커스를 받았을 때 처리 (Android Back 버튼 등)
    const resumeListener = App.addListener("appStateChange", (state) => {
      if (state.isActive) {
        // 앱이 포그라운드로 돌아왔을 때 추가 처리 필요 시 여기에 구현
        console.log("앱이 활성화됨");
      }
    });

    return () => {
      listener.then((l) => l.remove());
      resumeListener.then((l) => l.remove());
    };
  }, [handleDeepLink]);
}
